import { useState, useEffect, useCallback } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiConfirm, uiPrompt } from '../utils/dialogs.js';

// ── Permissions: one row per sidebar page (Joe, 27 Oct 2026) ─────────────────
// Keys match the server's page permissions. View = can see the page;
// Edit = can add and change things on it. Ticked actions need Edit.
const MODULES = [
  { key: 'production',          label: 'Production',          group: 'Operations' },
  { key: 'recipes',             label: 'Recipes',             group: 'Operations' },
  { key: 'stock',               label: 'Stock',               group: 'Operations' },
  { key: 'casks',               label: 'Casks',               group: 'Operations' },
  { key: 'storage_units',       label: 'Storage Units',       group: 'Operations' },
  { key: 'warehouse',           label: 'Warehouse Map',       group: 'Operations' },
  { key: 'stock_take',          label: 'Stock Take',          group: 'Operations' },
  { key: 'staff_tasks',         label: 'Staff Tasks',         group: 'Operations' },
  { key: 'production_schedule', label: 'Production Schedule', group: 'Operations' },
  { key: 'clients',             label: 'Contract Clients',    group: 'Commercial' },
  { key: 'own_brand',           label: 'Own Brand',           group: 'Commercial' },
  { key: 'orders',              label: 'Orders',              group: 'Commercial' },
  { key: 'quoting',             label: 'Quotes',              group: 'Commercial' },
  { key: 'recipe_development',  label: 'Recipe Development',  group: 'Commercial' },
  { key: 'billing',             label: 'Billing',             group: 'Commercial' },
  { key: 'product_specs',       label: 'Product Specs',       group: 'Commercial' },
  { key: 'outlets',             label: 'Outlets',             group: 'Commercial' },
  { key: 'hmrc',                label: 'HMRC',                group: 'Compliance' },
  { key: 'salsa',               label: 'SALSA',               group: 'Compliance' },
  { key: 'reports',             label: 'Reports',             group: 'Compliance' },
  { key: 'admin',               label: 'Settings',            group: 'Settings' },
];

// What each page covers, and the extra actions on it.
const MODULE_INFO = {
  production: { desc: 'Production runs, QC and strength checks.',
    actions: [['can_sign_off_runs', 'Approve sign-off of production runs (and recipe development rounds)']] },
  recipes: { desc: 'Production recipes and their stages and ingredients.' },
  stock: { desc: 'Stock items and levels, purchase orders, suppliers and goods in.',
    actions: [
      ['can_receive_stock', 'Receive stock (goods in, PO receiving)'],
      ['can_adjust_stock', 'Adjust stock levels'],
      ['can_write_off_stock', 'Write off stock'],
      ['can_manage_suppliers', 'Add and edit suppliers'],
    ] },
  casks: { desc: 'Casks and what\'s in them.' },
  storage_units: { desc: 'Pallets and other storage units, their contents, and consignments.' },
  warehouse: { desc: 'Warehouse map: facilities, zones and locations.' },
  stock_take: { desc: 'Stock takes and counts.',
    actions: [['can_adjust_stock', 'Sign off stock takes']] },
  staff_tasks: { desc: 'View: see and tick off tasks. Edit: set up tasks.' },
  production_schedule: { desc: 'The weekly production schedule board (also on the dashboard).' },
  clients: { desc: 'Contract clients, their contacts, client stock and goods-in links.',
    actions: [['can_receive_stock', 'Check in client stock']] },
  own_brand: { desc: 'Own-brand batches, recalls and price lists.',
    actions: [
      ['can_initiate_recall', 'Start a recall'],
      ['can_approve_recall', 'Approve a recall'],
    ] },
  orders: { desc: 'Sales and fulfilment orders, order files, the orders@ email inbox, despatch and fulfilment billing.' },
  quoting: { desc: 'Quotes for clients.',
    actions: [['can_approve_quotes', 'Approve quotes']] },
  recipe_development: { desc: 'Recipe development projects and sample rounds.' },
  billing: { desc: 'Storage billing and commission.',
    actions: [['can_manage_rates', 'Change storage and fulfilment rates']] },
  product_specs: { desc: 'Product specifications and safety data sheets.',
    actions: [['can_approve_specs', 'Approve product specifications']] },
  outlets: { desc: 'Outlets (trade customers), their price lists and discounts, and registrations.' },
  hmrc: { desc: 'W1 and APPA returns, duty rates and duty movements.',
    actions: [
      ['can_generate_hmrc', 'Recalculate, adjust and lock returns'],
      ['can_export_hmrc', 'Submit / export returns (needs 2FA)'],
      ['can_manage_rates', 'Manage duty rates'],
    ] },
  salsa: { desc: 'SALSA food-safety records and COSHH.' },
  reports: { desc: 'The Reports page. Each report also needs View on the page its figures come from.' },
  admin: { desc: 'Settings: users and permissions, company details, documents, printers, integrations, rates. Edit lets someone change other people\'s access — give it sparingly.' },
};
const LEVEL_LABEL = { none: 'None', read: 'View', write: 'Edit' };

// ── Helpers ───────────────────────────────────────────────────────────────────
function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function Badge({ active }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 99,
      fontSize: 11, fontWeight: 600,
      background: active ? '#dcfce7' : '#f3f4f6',
      color: active ? '#166534' : '#6b7280',
    }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────
// ── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: 480,
        maxHeight: '90vh', overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border)',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Drawer shell (slide-over panel, for "Manage user" and similar per-record
// admin views — everything about one record lives in one place, tabbed,
// rather than spread across a row of small buttons and separate modals) ──────
function Drawer({ title, subtitle, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,.4)',
      display: 'flex', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface)', width: '100%', maxWidth: 480,
        height: '100%', boxShadow: 'var(--shadow-lg)',
        display: 'flex', flexDirection: 'column',
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
            {subtitle && <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

function DrawerTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 0, padding: '0 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
      {tabs.map(t => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '12px 4px', marginRight: 20,
            fontSize: 13, fontWeight: 600,
            color: active === t.key ? 'var(--pld-green)' : 'var(--text-muted)',
            borderBottom: active === t.key ? '2px solid var(--pld-green)' : '2px solid transparent',
          }}
        >{t.label}</button>
      ))}
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────
function Field({ label, children, error, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: 'var(--status-critical)' }}>{error}</span>}
    </div>
  );
}

function Input(props) {
  return (
    <input {...props} style={{
      padding: '9px 12px', borderRadius: 'var(--radius)',
      border: '1px solid var(--border)', fontSize: 14,
      background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box',
      ...props.style,
    }} />
  );
}

function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer',
    border: 'none', transition: 'opacity .15s',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    danger:  { background: '#dc2626', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
  };
  return <button {...props} style={{ ...base, ...variants[variant], ...props.style }}>{children}</button>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: USERS
// ═══════════════════════════════════════════════════════════════════════════════
function UsersTab({ currentUser }) {
  const { data: users, loading, error, refetch } = useApi('/settings/users');
  const [showCreate, setShowCreate] = useState(false);
  const [manageUser, setManageUser] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState('');

  const [createForm, setCreateForm] = useState({ name: '', email: '', is_system_owner: false });
  // Result of the most recent invite send (create or resend) — shown once
  // in its own panel so Joe can see whether the email actually went out,
  // and gets the temp password directly if it didn't.
  const [inviteResult, setInviteResult] = useState(null);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true); setFormError('');
    try {
      const res = await apiFetch('/settings/users', { method: 'POST', body: createForm });
      setShowCreate(false);
      setCreateForm({ name: '', email: '', is_system_owner: false });
      setInviteResult({ email: createForm.email, ...res.invite });
      refetch();
    } catch (err) { setFormError(err.message); }
    finally { setSaving(false); }
  }

  // Only show the full-page loader on first load. A refetch after saving
  // must not unmount the page — that closed and reopened the Manage drawer
  // with the pre-save copy of the user, so saved edits looked like they'd
  // vanished (and any save error was wiped too).
  if (loading && !users) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading users…</div>;
  if (error && !users)   return <div style={{ padding: 32, color: 'var(--status-critical)' }}>Error: {error}</div>;

  // The drawer always shows the latest copy of the user from the list.
  const managedUser = manageUser ? (users?.find((u) => u.id === manageUser.id) ?? manageUser) : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>
          {users?.length ?? 0} user{users?.length !== 1 ? 's' : ''} — click a row to manage
        </p>
        {currentUser?.is_system_owner && (
          <Btn onClick={() => { setShowCreate(true); setFormError(''); }}>+ Add User</Btn>
        )}
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
              {['User', 'Email', 'Job title', 'Status', '2FA', 'Last login', ''].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(users || []).map((u, i) => (
              <tr
                key={u.id}
                onClick={() => setManageUser(u)}
                style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%',
                      background: 'var(--pld-green)', color: 'var(--pld-yellow)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, flexShrink: 0,
                    }}>{initials(u.name)}</div>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>{u.name}</span>
                    {u.is_system_owner && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--pld-green)', textTransform: 'uppercase', letterSpacing: '.04em', border: '1px solid var(--pld-green)', borderRadius: 4, padding: '1px 5px' }}>Owner</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-muted)' }}>{u.email}</td>
                <td style={{ padding: '12px 16px', color: u.job_title ? 'var(--text)' : 'var(--text-muted)', fontSize: 13 }}>
                  {u.job_title || '—'}
                </td>
                <td style={{ padding: '12px 16px' }}><Badge active={u.is_active} /></td>
                <td style={{ padding: '12px 16px', fontSize: 13 }}>{u.totp_enabled ? '✅' : '—'}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 13 }}>
                  {u.last_login_at
                    ? new Date(u.last_login_at).toLocaleDateString()
                    : u.must_change_password
                      ? <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--pld-green)', textTransform: 'uppercase', letterSpacing: '.03em' }}>Invite pending</span>
                      : 'Never'}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <Btn size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setManageUser(u); }}>Manage →</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal title="Add User" onClose={() => { setShowCreate(false); setFormError(''); }}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Full name"><Input required value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} /></Field>
            <Field label="Email" hint="A temporary password gets emailed here — they'll set their own on first login"><Input required type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} /></Field>
            {currentUser?.is_system_owner && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={createForm.is_system_owner} onChange={e => setCreateForm(f => ({ ...f, is_system_owner: e.target.checked }))} />
                System Owner (full access)
              </label>
            )}
            {formError && <p style={{ margin: 0, color: 'var(--status-critical)', fontSize: 13 }}>{formError}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create User'}</Btn>
            </div>
          </form>
        </Modal>
      )}

      {managedUser && (
        <ManageUserDrawer
          user={managedUser}
          currentUser={currentUser}
          onClose={() => setManageUser(null)}
          onChanged={refetch}
          onInviteResult={setInviteResult}
        />
      )}

      {inviteResult && (
        <Modal title="Invite sent" onClose={() => setInviteResult(null)}>
          {inviteResult.emailed ? (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>
              An invite with a temporary password was emailed to <strong>{inviteResult.email}</strong>. They'll be asked to set their own password the first time they log in.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--status-critical)' }}>
                Couldn't email the invite to {inviteResult.email}{inviteResult.emailError ? ` (${inviteResult.emailError})` : ''}. Pass this temporary password to them yourself — they'll be asked to set their own on first login:
              </p>
              <div style={{
                fontFamily: 'monospace', fontSize: 16, fontWeight: 700, letterSpacing: '.03em',
                background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '12px 16px', textAlign: 'center', userSelect: 'all',
              }}>
                {inviteResult.tempPassword}
              </div>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                Once email sending is set up, use "Resend invite" (in Manage → Security) to try again instead of sharing this manually.
              </p>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn onClick={() => setInviteResult(null)}>Done</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Manage user drawer ───────────────────────────────────────────────────────
// One entry point per user (click their row, or "Manage →") for everything
// admin-related about their account: profile, module permissions, and
// security actions (password reset, resend invite, activate/deactivate) —
// instead of a row of small separate buttons and modals.
function ManageUserDrawer({ user, currentUser, onClose, onChanged, onInviteResult }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [isActive, setIsActive]     = useState(user.is_active);
  const [mustChange, setMustChange] = useState(user.must_change_password);

  const [profileForm, setProfileForm]   = useState({ name: user.name, email: user.email, job_title: user.job_title ?? '', phone: user.phone ?? '' });
  const [profileSaved, setProfileSaved] = useState(false);

  const [pwForm, setPwForm]   = useState({ newPassword: '', currentPassword: '' });
  const [pwSaved, setPwSaved] = useState(false);
  const [resending, setResending] = useState(false);

  const isSelf     = user.id === currentUser?.id;
  const canManage  = !!currentUser?.is_system_owner;

  async function handleSaveProfile(e) {
    e.preventDefault(); setSaving(true); setError(''); setProfileSaved(false);
    try {
      await apiFetch(`/settings/users/${user.id}`, { method: 'PUT', body: profileForm });
      setProfileSaved(true);
      onChanged();
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleToggleActive() {
    setSaving(true); setError('');
    try {
      await apiFetch(`/settings/users/${user.id}`, { method: 'PUT', body: { is_active: !isActive } });
      setIsActive(a => !a);
      onChanged();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!(await uiConfirm(`Permanently delete ${user.name}'s account? This can't be undone. If they have any history in the system (audit trail, records they created), this will be refused and you should deactivate instead.`))) {
      return;
    }
    setSaving(true); setError('');
    try {
      await apiFetch(`/settings/users/${user.id}`, { method: 'DELETE' });
      onChanged();
      onClose();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleResetPw(e) {
    e.preventDefault(); setSaving(true); setError(''); setPwSaved(false);
    try {
      await apiFetch(`/settings/users/${user.id}/password`, { method: 'PUT', body: pwForm });
      setPwForm({ newPassword: '', currentPassword: '' });
      setPwSaved(true);
      if (!isSelf) setMustChange(true);
      onChanged();
      setTimeout(() => setPwSaved(false), 2500);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function handleResendInvite() {
    setResending(true); setError('');
    try {
      const res = await apiFetch(`/settings/users/${user.id}/resend-invite`, { method: 'POST' });
      onInviteResult({ email: user.email, ...res.invite });
      onChanged();
    } catch (err) { setError(err.message); }
    finally { setResending(false); }
  }

  const tabs = [
    { key: 'profile', label: 'Profile' },
    ...(user.is_system_owner ? [] : [{ key: 'permissions', label: 'Permissions' }]),
    { key: 'security', label: 'Security' },
  ];

  return (
    <Drawer title={user.name} subtitle={user.email} onClose={onClose}>
      <DrawerTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {error && <p style={{ margin: 0, color: 'var(--status-critical)', fontSize: 13 }}>{error}</p>}

        {activeTab === 'profile' && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Badge active={isActive} />
              {user.is_system_owner && (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--pld-green)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Owner</span>
              )}
              {mustChange && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '.03em' }}>Invite pending</span>
              )}
            </div>

            <form onSubmit={handleSaveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Full name"><Input required value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} /></Field>
              <Field label="Email"><Input required type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))} /></Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Job title"><Input value={profileForm.job_title} onChange={e => setProfileForm(f => ({ ...f, job_title: e.target.value }))} placeholder="e.g. Operations Manager" /></Field>
                <Field label="Direct phone"><Input type="tel" value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} placeholder="e.g. 07700 900123" /></Field>
              </div>
              <p style={{ margin: '-6px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>Name, job title, phone and email appear in the sign-off of emails Blake sends for this person, such as purchase orders.</p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Btn type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</Btn>
                {profileSaved && <span style={{ fontSize: 13, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Saved</span>}
              </div>
            </form>

            {canManage && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 4 }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em' }}>Danger zone</p>
                {isSelf ? (
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>You can't deactivate your own account.</p>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" variant={isActive ? 'danger' : 'ghost'} disabled={saving} onClick={handleToggleActive}>
                      {isActive ? 'Deactivate account' : 'Reactivate account'}
                    </Btn>
                    <Btn size="sm" variant="danger" disabled={saving} onClick={handleDelete}>
                      Delete permanently
                    </Btn>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'permissions' && (
          <PermissionsEditor userId={user.id} />
        )}

        {activeTab === 'security' && (
          <>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Two-factor authentication</p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{user.totp_enabled ? 'Enabled' : 'Not enabled'}</p>
            </div>

            {mustChange ? (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>This account hasn't set its own password yet.</p>
                <Btn size="sm" variant="ghost" disabled={resending} onClick={handleResendInvite} style={{ alignSelf: 'flex-start' }}>
                  {resending ? 'Sending…' : 'Resend invite'}
                </Btn>
              </div>
            ) : !isSelf && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
                  Need to reset their access? This emails them a fresh temporary password and asks them to set a new one the next time they log in.
                </p>
                <Btn size="sm" variant="ghost" disabled={resending} onClick={handleResendInvite} style={{ alignSelf: 'flex-start' }}>
                  {resending ? 'Sending…' : 'Send invite'}
                </Btn>
              </div>
            )}

            <form onSubmit={handleResetPw} style={{ display: 'flex', flexDirection: 'column', gap: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Reset password</p>
              {isSelf && (
                <Field label="Current password">
                  <Input required type="password" value={pwForm.currentPassword} onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} />
                </Field>
              )}
              <Field label="New password" hint={!isSelf ? "They'll be asked to change this on next login" : undefined}>
                <Input required type="password" minLength={8} value={pwForm.newPassword} onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Min. 8 characters" />
              </Field>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Btn type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Update password'}</Btn>
                {pwSaved && <span style={{ fontSize: 13, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Updated</span>}
              </div>
            </form>
          </>
        )}
      </div>
    </Drawer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: PERMISSIONS
// ═══════════════════════════════════════════════════════════════════════════════
// The module/access-level grid itself lives in PermissionsEditor so it can be
// reused both here (with a user picker, for a dedicated permissions view) and
// inline inside the "Manage user" drawer's Permissions tab.
function PermissionsEditor({ userId }) {
  const [matrix, setMatrix]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setSaved(false); setMatrix(null);
    apiFetch(`/settings/users/${userId}/permissions`)
      .then(res => { if (!cancelled) setMatrix(res.data.matrix); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  function updateRow(module, field, value) {
    setMatrix(prev => prev.map(r => r.module === module ? { ...r, [field]: value } : r));
  }

  function toggleAction(module, key, on) {
    setMatrix(prev => prev.map(r => r.module === module
      ? { ...r, action_permissions: { ...(r.action_permissions || {}), [key]: on } }
      : r));
  }

  function setAll(level) {
    setMatrix(prev => prev.map(r => ({ ...r, access_level: level })));
  }

  async function handleSave() {
    setSaving(true); setError(''); setSaved(false);
    try {
      await apiFetch(`/settings/users/${userId}/permissions`, {
        method: 'PUT',
        body: { permissions: matrix },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const levelColor = { none: '#e5e7eb', read: '#dbeafe', write: '#dcfce7' };
  const levelText  = { none: '#6b7280', read: '#1d4ed8', write: '#166534' };

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>Loading permissions…</p>;
  if (error)   return <p style={{ color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{error}</p>;
  if (!matrix) return null;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>One row per page in the sidebar. None = page hidden · View = can see it · Edit = can add and change things on it. Ticked actions need Edit on that page.</span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Set all:</span>
        {['none', 'read', 'write'].map(l => (
          <Btn key={l} size="sm" variant="ghost" onClick={() => setAll(l)}>{LEVEL_LABEL[l]}</Btn>
        ))}
      </div>

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>Page — what it covers, and extra actions</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>Access</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>Require 2FA</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, i) => {
              const modLabel = MODULES.find(m => m.key === row.module)?.label ?? row.module;
              return (
                <tr key={row.module} style={{ borderBottom: i < matrix.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--text)', verticalAlign: 'top', maxWidth: 460 }}>
                    <div style={{ fontWeight: 600 }}>{modLabel}</div>
                    {MODULE_INFO[row.module]?.desc && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>{MODULE_INFO[row.module].desc}</div>}
                    {MODULE_INFO[row.module]?.actions && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {MODULE_INFO[row.module].actions.map(([key, label]) => (
                          <label key={key} title={row.access_level !== 'write' ? 'Needs Edit on this page' : ''}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12.5, cursor: row.access_level === 'write' ? 'pointer' : 'not-allowed', opacity: row.access_level === 'write' ? 1 : 0.5 }}>
                            <input type="checkbox" style={{ marginTop: 2 }} disabled={row.access_level !== 'write'}
                              checked={!!row.action_permissions?.[key]}
                              onChange={e => toggleAction(row.module, key, e.target.checked)} />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', verticalAlign: 'top' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['none', 'read', 'write'].map(level => (
                        <button
                          key={level}
                          onClick={() => updateRow(row.module, 'access_level', level)}
                          style={{
                            padding: '4px 12px', borderRadius: 99, border: 'none', cursor: 'pointer',
                            fontSize: 12, fontWeight: 600,
                            background: row.access_level === level ? levelColor[level] : 'var(--surface-2)',
                            color: row.access_level === level ? levelText[level] : 'var(--text-muted)',
                            outline: row.access_level === level ? `2px solid ${levelText[level]}40` : 'none',
                          }}
                        >{LEVEL_LABEL[level]}</button>
                      )
)}
                    </div>
                  </td>
                  <td style={{ padding: '10px 16px', verticalAlign: 'top' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={row.requires_2fa}
                        onChange={e => updateRow(row.module, 'requires_2fa', e.target.checked)}
                      />
                      Yes
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Btn onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Permissions'}</Btn>
        {saved && <span style={{ fontSize: 13, color: 'var(--pld-green)', fontWeight: 600 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

function PermissionsTab({ currentUser }) {
  const { data: users, loading: usersLoading } = useApi('/settings/users');
  const [selectedUserId, setSelectedUserId] = useState('');

  const staffUsers = (users || []).filter(u => !u.is_system_owner);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
          Select user to manage permissions
        </label>
        {usersLoading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
        ) : (
          <select
            value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
            style={{
              padding: '9px 12px', borderRadius: 'var(--radius)',
              border: '1px solid var(--border)', fontSize: 14,
              background: 'var(--surface-2)', minWidth: 280,
            }}
          >
            <option value="">— Select a user —</option>
            {staffUsers.map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>
        )}
        {staffUsers.length === 0 && !usersLoading && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>
            No staff users yet. Create users in the Users tab first.
          </p>
        )}
        {!usersLoading && staffUsers.length > 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>
            Tip: you can also manage a user's permissions from Users → click their row → Permissions tab.
          </p>
        )}
      </div>

      {selectedUserId && <PermissionsEditor userId={selectedUserId} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: MY 2FA
// ═══════════════════════════════════════════════════════════════════════════════
function QRImage({ url }) {
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  return <img src={src} alt="2FA QR Code" width={200} height={200} style={{ display: 'block' }} />;
}

function TwoFATab({ currentUser, onUserUpdate }) {
  const [step, setStep]     = useState('idle');
  const [qrUrl, setQrUrl]   = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [code, setCode]     = useState('');
  const [password, setPw]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  const is2FAEnabled = currentUser?.totp_enabled;

  async function startSetup() {
    setLoading(true); setError('');
    try {
      const res = await apiFetch('/auth/2fa/setup', { method: 'POST' });
      setQrUrl(res.otpauthUrl || res.otpAuthUrl || res.data?.otpauthUrl || '');
      setBackupCodes(res.backupCodes || res.data?.backupCodes || []);
      setStep('setup');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function confirmSetup() {
    if (!code || code.length < 6) { setError('Enter the 6-digit code from your authenticator app'); return; }
    setLoading(true); setError('');
    try {
      await apiFetch('/auth/2fa/confirm', { method: 'POST', body: { totpCode: code } });
      setStep('idle'); setCode(''); setSuccess('2FA enabled successfully.');
      onUserUpdate();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function disableSetup() {
    if (!code || code.length < 6) { setError('Enter your current 6-digit TOTP code'); return; }
    if (!password) { setError('Enter your password'); return; }
    setLoading(true); setError('');
    try {
      await apiFetch('/auth/2fa/disable', { method: 'POST', body: { totpCode: code, password } });
      setStep('idle'); setCode(''); setPw(''); setSuccess('2FA has been disabled.');
      onUserUpdate();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <div style={{
        padding: 20, borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)',
        marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{ fontSize: 32 }}>{is2FAEnabled ? '🔐' : '🔓'}</div>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            Two-Factor Authentication is{' '}
            <span style={{ color: is2FAEnabled ? 'var(--pld-green)' : '#dc2626' }}>
              {is2FAEnabled ? 'enabled' : 'disabled'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {is2FAEnabled
              ? 'Your account is protected with TOTP authentication.'
              : 'Enable 2FA to add an extra layer of security to your account.'}
          </div>
        </div>
      </div>

      {success && <div style={{ padding: '10px 14px', background: '#dcfce7', borderRadius: 'var(--radius)', color: '#166534', fontSize: 14, marginBottom: 16 }}>{success}</div>}
      {error   && <div style={{ padding: '10px 14px', background: '#fef2f2', borderRadius: 'var(--radius)', color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>}

      {step === 'idle' && !is2FAEnabled && (
        <Btn onClick={startSetup} disabled={loading}>{loading ? 'Loading…' : 'Enable 2FA'}</Btn>
      )}

      {step === 'idle' && is2FAEnabled && (
        <Btn variant="danger" onClick={() => { setStep('disable'); setError(''); setSuccess(''); }}>
          Disable 2FA
        </Btn>
      )}

      {step === 'setup' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--text)' }}>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.):
            </p>
            {qrUrl && (
              <div style={{ background: '#fff', padding: 12, borderRadius: 8, display: 'inline-block', border: '1px solid var(--border)' }}>
                <QRImage url={qrUrl} />
              </div>
            )}
          </div>

          {backupCodes.length > 0 && (
            <div style={{ background: 'var(--surface-2)', padding: 16, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                ⚠️ Save these backup codes — they won't be shown again:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {backupCodes.map((c, i) => (
                  <code key={i} style={{ fontSize: 13, color: 'var(--text)', fontFamily: 'monospace' }}>{c}</code>
                ))}
              </div>
            </div>
          )}

          <Field label="Enter the 6-digit code from your app:">
            <Input
              type="text" inputMode="numeric" maxLength={6}
              value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456" style={{ maxWidth: 160 }}
            />
          </Field>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={() => { setStep('idle'); setCode(''); setError(''); }}>Cancel</Btn>
            <Btn onClick={confirmSetup} disabled={loading}>{loading ? 'Verifying…' : 'Verify & Enable'}</Btn>
          </div>
        </div>
      )}

      {step === 'disable' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text)' }}>
            To disable 2FA, confirm your password and current authenticator code:
          </p>
          <Field label="Password">
            <Input type="password" value={password} onChange={e => setPw(e.target.value)} />
          </Field>
          <Field label="Authenticator code (6 digits)">
            <Input type="text" inputMode="numeric" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456" style={{ maxWidth: 160 }} />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={() => { setStep('idle'); setCode(''); setPw(''); setError(''); }}>Cancel</Btn>
            <Btn variant="danger" onClick={disableSetup} disabled={loading}>{loading ? 'Disabling…' : 'Disable 2FA'}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// WAREHOUSE TAB
// ═══════════════════════════════════════════════════════════════════════════════
const ZONE_TYPES = ['bonded', 'non_bonded', 'production', 'cask_store', 'dry_store', 'external'];
const LOC_TYPES  = ['rack', 'floor', 'ibc_bay', 'cask_bay'];

function fmtType(t) {
  return t ? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
}

function Textarea({ value, onChange, rows = 3, placeholder }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      style={{
        padding: '9px 12px', borderRadius: 'var(--radius)',
        border: '1px solid var(--border)', fontSize: 14,
        background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box',
        fontFamily: 'inherit', resize: 'vertical',
      }}
    />
  );
}

function SelectEl({ value, onChange, children, required }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      required={required}
      style={{
        padding: '9px 12px', borderRadius: 'var(--radius)',
        border: '1px solid var(--border)', fontSize: 14,
        background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box',
        color: 'var(--text)',
      }}
    >{children}</select>
  );
}

function FacilityModal({ facility, onSave, onClose }) {
  const [form, setForm] = useState({ name: facility?.name || '', address: facility?.address || '' });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const upd = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const path   = facility ? `/warehouse/facilities/${facility.id}` : '/warehouse/facilities';
      const method = facility ? 'PUT' : 'POST';
      const res = await apiFetch(path, { method, body: form });
      onSave(res.facility);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={facility ? 'Edit Facility' : 'Add Facility'} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <p style={{ color: 'red', margin: 0, fontSize: 13 }}>{error}</p>}
        <Field label="Name *"><Input value={form.name} onChange={e => upd('name')(e.target.value)} placeholder="e.g. Main Warehouse" required /></Field>
        <Field label="Address"><Textarea value={form.address} onChange={upd('address')} rows={3} placeholder="Full address" /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} type="button">Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function ZoneModal({ zone, facilityId, onSave, onClose }) {
  const [form, setForm] = useState({
    name: zone?.name || '', code: zone?.code || '', zone_type: zone?.zone_type || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const upd = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const path   = zone ? `/warehouse/zones/${zone.id}` : '/warehouse/zones';
      const method = zone ? 'PUT' : 'POST';
      const body   = zone ? form : { ...form, facility_id: facilityId };
      const res = await apiFetch(path, { method, body });
      onSave(res.zone);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={zone ? 'Edit Zone' : 'Add Zone'} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <p style={{ color: 'red', margin: 0, fontSize: 13 }}>{error}</p>}
        <Field label="Name *"><Input value={form.name} onChange={e => upd('name')(e.target.value)} placeholder="e.g. Bonded Store A" required /></Field>
        <Field label="Code *"><Input value={form.code} onChange={e => upd('code')(e.target.value)} placeholder="e.g. BSA" required /></Field>
        <Field label="Zone Type *">
          <SelectEl value={form.zone_type} onChange={upd('zone_type')} required>
            <option value="">Select type…</option>
            {ZONE_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
          </SelectEl>
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} type="button">Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function LocationModal({ location, zoneId, onSave, onClose }) {
  const [form, setForm] = useState({
    code: location?.code || '',
    location_type: location?.location_type || '',
    row_ref:       location?.row_ref || '',
    bay_ref:       location?.bay_ref || '',
    level_ref:     location?.level_ref || '',
    grid_ref:      location?.grid_ref || '',
    max_pallets:   location?.max_pallets ?? '',
    max_weight_kg: location?.max_weight_kg ?? '',
    max_height_mm: location?.max_height_mm ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const upd = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const path   = location ? `/warehouse/locations/${location.id}` : '/warehouse/locations';
      const method = location ? 'PUT' : 'POST';
      const payload = { ...form };
      if (!location) payload.zone_id = zoneId;
      ['max_pallets', 'max_weight_kg', 'max_height_mm'].forEach(k => {
        payload[k] = payload[k] === '' ? null : Number(payload[k]);
      });
      const res = await apiFetch(path, { method, body: payload });
      onSave(res.location);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={location ? 'Edit Location' : 'Add Location'} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <p style={{ color: 'red', margin: 0, fontSize: 13 }}>{error}</p>}
        <Field label="Code *"><Input value={form.code} onChange={e => upd('code')(e.target.value)} placeholder="e.g. A-01-03" required /></Field>
        <Field label="Type *">
          <SelectEl value={form.location_type} onChange={upd('location_type')} required>
            <option value="">Select type…</option>
            {LOC_TYPES.map(t => <option key={t} value={t}>{fmtType(t)}</option>)}
          </SelectEl>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Row"><Input value={form.row_ref} onChange={e => upd('row_ref')(e.target.value)} placeholder="A" /></Field>
          <Field label="Bay"><Input value={form.bay_ref} onChange={e => upd('bay_ref')(e.target.value)} placeholder="01" /></Field>
          <Field label="Level"><Input value={form.level_ref} onChange={e => upd('level_ref')(e.target.value)} placeholder="03" /></Field>
          <Field label="Grid Ref"><Input value={form.grid_ref} onChange={e => upd('grid_ref')(e.target.value)} placeholder="A01-03" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Max Pallets"><Input type="number" value={form.max_pallets} onChange={e => upd('max_pallets')(e.target.value)} placeholder="—" /></Field>
          <Field label="Max kg"><Input type="number" value={form.max_weight_kg} onChange={e => upd('max_weight_kg')(e.target.value)} placeholder="—" /></Field>
          <Field label="Max mm (H)"><Input type="number" value={form.max_height_mm} onChange={e => upd('max_height_mm')(e.target.value)} placeholder="—" /></Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} type="button">Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function WarehouseTab() {
  const [view, setView]         = useState('facilities');
  const [facility, setFacility] = useState(null);
  const [zone, setZone]         = useState(null);
  const [modal, setModal]       = useState(null);

  const { data: facData, refetch: refetchFac } = useApi('/warehouse/facilities');
  const facilities = facData?.facilities ?? [];

  const { data: zoneData, refetch: refetchZone } = useApi(
    facility ? `/warehouse/zones?facility_id=${facility.id}` : null,
    [facility?.id]
  );
  const zones = zoneData?.zones ?? [];

  const { data: locData, refetch: refetchLoc } = useApi(
    zone ? `/warehouse/locations?zone_id=${zone.id}` : null,
    [zone?.id]
  );
  const locations = locData?.locations ?? [];

  function drillZones(fac) { setFacility(fac); setZone(null); setView('zones'); }
  function drillLocs(z)    { setZone(z); setView('locations'); }
  function backFac()        { setFacility(null); setZone(null); setView('facilities'); }
  function backZone()       { setZone(null); setView('zones'); }

  const tagStyle = color => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 99,
    fontSize: 11, fontWeight: 600,
    background: color + '22', color,
  });

  const rowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', background: 'var(--surface)',
    border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8,
    cursor: 'pointer',
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 13 }}>
        <span
          style={{ fontWeight: 600, color: view === 'facilities' ? 'var(--text)' : 'var(--pld-green)', cursor: view !== 'facilities' ? 'pointer' : 'default' }}
          onClick={view !== 'facilities' ? backFac : undefined}
        >Facilities</span>
        {facility && (
          <>
            <span style={{ color: 'var(--text-muted)' }}>›</span>
            <span
              style={{ fontWeight: 600, color: view === 'zones' ? 'var(--text)' : 'var(--pld-green)', cursor: view === 'locations' ? 'pointer' : 'default' }}
              onClick={view === 'locations' ? backZone : undefined}
            >{facility.name}</span>
          </>
        )}
        {zone && (
          <>
            <span style={{ color: 'var(--text-muted)' }}>›</span>
            <span style={{ fontWeight: 600, color: 'var(--text)' }}>{zone.name}</span>
          </>
        )}
      </div>

      {/* Facilities */}
      {view === 'facilities' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Btn size="sm" onClick={() => setModal({ type: 'facility' })}>+ Add Facility</Btn>
          </div>
          {facilities.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>No facilities yet. Add one to get started.</p>
          )}
          {facilities.map(f => (
            <div key={f.id} style={rowStyle} onClick={() => drillZones(f)}>
              <div>
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{f.name}</div>
                {f.address && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{f.address}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Btn size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setModal({ type: 'facility', item: f }); }}>Edit</Btn>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>View zones ›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zones */}
      {view === 'zones' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Btn size="sm" onClick={() => setModal({ type: 'zone' })}>+ Add Zone</Btn>
          </div>
          {zones.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>No zones in this facility yet.</p>
          )}
          {zones.map(z => (
            <div key={z.id} style={rowStyle} onClick={() => drillLocs(z)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{z.name}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 6 }}>({z.code})</span>
                  <div style={{ marginTop: 4 }}><span style={tagStyle('var(--pld-green)')}>{fmtType(z.zone_type)}</span></div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Btn size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setModal({ type: 'zone', item: z }); }}>Edit</Btn>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>View locations ›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Locations */}
      {view === 'locations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Btn size="sm" onClick={() => setModal({ type: 'location' })}>+ Add Location</Btn>
          </div>
          {locations.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>No locations in this zone yet.</p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            {locations.map(l => (
              <div key={l.id} style={{
                padding: 16, background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{l.code}</span>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ type: 'location', item: l })}>Edit</Btn>
                </div>
                <span style={tagStyle('#6366f1')}>{fmtType(l.location_type)}</span>
                {(l.row_ref || l.bay_ref || l.level_ref) && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {[l.row_ref && `Row ${l.row_ref}`, l.bay_ref && `Bay ${l.bay_ref}`, l.level_ref && `Level ${l.level_ref}`].filter(Boolean).join(' · ')}
                  </div>
                )}
                {l.max_pallets && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Max {l.max_pallets} pallet{l.max_pallets !== 1 ? 's' : ''}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'facility' && (
        <FacilityModal facility={modal.item} onSave={() => { setModal(null); refetchFac(); }} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'zone' && (
        <ZoneModal zone={modal.item} facilityId={facility?.id} onSave={() => { setModal(null); refetchZone(); }} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'location' && (
        <LocationModal location={modal.item} zoneId={zone?.id} onSave={() => { setModal(null); refetchLoc(); }} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: RATES & BILLING
// ═══════════════════════════════════════════════════════════════════════════════
// Storage rates have their own shape (bonded + unbonded £/pallet/week) that
// doesn't match the generic rate_type/rate_per_unit form below — the backend
// (storage_rates table + POST /commercial/storage-rates) has always expected
// rate_bonded_per_pallet_per_week / rate_unbonded_per_pallet_per_week, so the
// old shared form was silently unusable for storage (it would 500 on submit).
function StorageRateModal({ onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const [form, setForm] = useState({
    client_id: '',
    rate_bonded_per_pallet_per_week: '',
    rate_unbonded_per_pallet_per_week: '',
    effective_from: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch('/commercial/storage-rates', {
        method: 'POST',
        body: {
          client_id: form.client_id || undefined,
          rate_bonded_per_pallet_per_week: parseFloat(form.rate_bonded_per_pallet_per_week),
          rate_unbonded_per_pallet_per_week: parseFloat(form.rate_unbonded_per_pallet_per_week),
          effective_from: form.effective_from,
          notes: form.notes || undefined,
        },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Add Storage Rate" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <p style={{ color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{error}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Client (leave blank for the default rate)</label>
          <SelectEl value={form.client_id} onChange={v => setForm(f => ({ ...f, client_id: v }))}>
            <option value="">Default rate (all clients)</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectEl>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Setting a rate for a client here replaces their current rate — it doesn't stack with the default.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Bonded £/pallet/week *</label>
            <Input required type="number" min="0" step="0.0001" value={form.rate_bonded_per_pallet_per_week} onChange={set('rate_bonded_per_pallet_per_week')} placeholder="e.g. 3.50" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Unbonded £/pallet/week *</label>
            <Input required type="number" min="0" step="0.0001" value={form.rate_unbonded_per_pallet_per_week} onChange={set('rate_unbonded_per_pallet_per_week')} placeholder="e.g. 3.00" />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Effective from</label>
          <Input type="date" value={form.effective_from} onChange={set('effective_from')} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Notes</label>
          <Textarea value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} rows={2} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Rate'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// fulfilment_rates columns: handling_fee_per_order (notNullable), packaging_markup_pct,
// shipping_pass_through, effective_from, notes. (migration 012_commercial_storage.js)
function FulfilmentRateModal({ rate, onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  // Editing a rate that's in (Joe, 3 Nov 2026): orders not billed yet can take the new fee.
  const { data: unbilledData } = useApi(rate?.is_current ? `/commercial/fulfilment-rates/${rate.id}/unbilled` : null);
  const unbilled = Number((unbilledData?.data ?? unbilledData)?.orders || 0);
  const [applyUnbilled, setApplyUnbilled] = useState(true);
  const [form, setForm] = useState(rate ? {
    client_id: rate.client_id || '',
    handling_fee_per_order: String(Number(rate.handling_fee_per_order)),
    packaging_markup_pct: String(Number(rate.packaging_markup_pct || 0)),
    shipping_pass_through: !!rate.shipping_pass_through,
    effective_from: rate.effective_from ? String(rate.effective_from).slice(0, 10) : new Date().toISOString().slice(0, 10),
    notes: rate.notes || '',
  } : {
    client_id: '',
    handling_fee_per_order: '',
    packaging_markup_pct: '0',
    shipping_pass_through: true,
    effective_from: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = {
        client_id: form.client_id || undefined,
        handling_fee_per_order: parseFloat(form.handling_fee_per_order),
        packaging_markup_pct: form.packaging_markup_pct === '' ? 0 : parseFloat(form.packaging_markup_pct),
        shipping_pass_through: form.shipping_pass_through,
        effective_from: form.effective_from,
        notes: form.notes || (rate ? null : undefined),
      };
      if (rate) await apiFetch(`/commercial/fulfilment-rates/${rate.id}`, { method: 'PUT', body: { ...body, apply_to_unbilled: unbilled > 0 && applyUnbilled } });
      else await apiFetch('/commercial/fulfilment-rates', { method: 'POST', body });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={rate ? `Edit Fulfilment Rate — ${rate.client_name || 'Default (all clients)'}` : 'Add Fulfilment Rate'} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <p style={{ color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{error}</p>}
        <div style={{ display: rate ? 'none' : 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Client (leave blank for default rate)</label>
          <SelectEl value={form.client_id} onChange={v => setForm(f => ({ ...f, client_id: v }))}>
            <option value="">Default rate (all clients)</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectEl>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Fulfilment fee £/parcel *</label>
            <Input required type="number" min="0" step="0.01" value={form.handling_fee_per_order} onChange={set('handling_fee_per_order')} placeholder="e.g. 3.50" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Packaging markup %</label>
            <Input type="number" min="0" step="0.01" value={form.packaging_markup_pct} onChange={set('packaging_markup_pct')} placeholder="e.g. 10" />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={form.shipping_pass_through} onChange={e => setForm(f => ({ ...f, shipping_pass_through: e.target.checked }))} />
          Pass DPD shipping cost through at cost
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Effective from</label>
          <Input type="date" value={form.effective_from} onChange={set('effective_from')} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Notes</label>
          <Textarea value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} rows={2} />
        </div>
        {rate && unbilled > 0 && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, background: 'var(--surface-2)', padding: '8px 10px', borderRadius: 'var(--radius)' }}>
            <input type="checkbox" checked={applyUnbilled} onChange={e => setApplyUnbilled(e.target.checked)} style={{ marginTop: 2 }} />
            <span>Also use the new fee on the {unbilled} order{unbilled === 1 ? '' : 's'} not billed yet{rate.client_id ? '' : ' (clients on the default rate)'}. Orders already on a bill keep what they were charged.</span>
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Rate'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// contract_production_rates columns: rate_per_litre_distilled, rate_per_litre_bottled,
// rate_per_hour, minimum_run_charge, bottling_rate_per_unit (all nullable),
// effective_from, notes. (migration 012_commercial_storage.js)
function ProductionRateModal({ onClose, onSaved }) {
  const { data: clientData } = useApi('/clients');
  const clients = clientData?.clients ?? clientData ?? [];
  const [form, setForm] = useState({
    client_id: '',
    rate_per_litre_distilled: '',
    rate_per_litre_bottled: '',
    rate_per_hour: '',
    minimum_run_charge: '',
    bottling_rate_per_unit: '',
    effective_from: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const num = v => (v === '' ? undefined : parseFloat(v));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch('/commercial/production-rates', {
        method: 'POST',
        body: {
          client_id: form.client_id || undefined,
          rate_per_litre_distilled: num(form.rate_per_litre_distilled),
          rate_per_litre_bottled: num(form.rate_per_litre_bottled),
          rate_per_hour: num(form.rate_per_hour),
          minimum_run_charge: num(form.minimum_run_charge),
          bottling_rate_per_unit: num(form.bottling_rate_per_unit),
          effective_from: form.effective_from,
          notes: form.notes || undefined,
        },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Add Contract Production Rate" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <p style={{ color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{error}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Client (leave blank for default rate)</label>
          <SelectEl value={form.client_id} onChange={v => setForm(f => ({ ...f, client_id: v }))}>
            <option value="">Default rate (all clients)</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </SelectEl>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
            Leave any rate field below blank if it doesn't apply — at least one should usually be set.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>£/litre distilled</label>
            <Input type="number" min="0" step="0.0001" value={form.rate_per_litre_distilled} onChange={set('rate_per_litre_distilled')} placeholder="e.g. 4.25" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>£/litre bottled</label>
            <Input type="number" min="0" step="0.0001" value={form.rate_per_litre_bottled} onChange={set('rate_per_litre_bottled')} placeholder="e.g. 1.10" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>£/hour</label>
            <Input type="number" min="0" step="0.01" value={form.rate_per_hour} onChange={set('rate_per_hour')} placeholder="e.g. 45.00" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Minimum run charge £</label>
            <Input type="number" min="0" step="0.01" value={form.minimum_run_charge} onChange={set('minimum_run_charge')} placeholder="e.g. 250.00" />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Bottling rate £/unit</label>
          <Input type="number" min="0" step="0.0001" value={form.bottling_rate_per_unit} onChange={set('bottling_rate_per_unit')} placeholder="e.g. 0.35" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Effective from</label>
          <Input type="date" value={form.effective_from} onChange={set('effective_from')} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>Notes</label>
          <Textarea value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} rows={2} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Rate'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function RatesSection({ title, endpoint, type, refetchKey }) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data, loading, refetch } = useApi(endpoint, [refetchKey]);
  // useApi already unwraps {success, data} responses down to the array
  // itself, so `data` here IS the list — data?.data was always undefined.
  const rates = Array.isArray(data) ? data : (data?.data ?? data?.rates ?? []);
  const isStorage = type === 'storage';
  const isFulfilment = type === 'fulfilment';
  return (
    <div style={{ marginBottom: 32 }}>
      {showCreate && (
        isStorage
          ? <StorageRateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />
          : isFulfilment
          ? <FulfilmentRateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />
          : <ProductionRateModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />
      )}
      {editing && <FulfilmentRateModal rate={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{title}</h3>
        <Btn size="sm" onClick={() => setShowCreate(true)}>+ Add Rate</Btn>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p> : rates.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No rates configured yet.</p>
      ) : isStorage ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Client', 'Bonded £/pallet/wk', 'Unbonded £/pallet/wk', 'Effective from', 'Current', 'Notes'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 600 }}>{r.client_name || 'Default (all clients)'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>£{Number(r.rate_bonded_per_pallet_per_week).toFixed(4)}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>£{Number(r.rate_unbonded_per_pallet_per_week).toFixed(4)}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{r.effective_from ? new Date(r.effective_from).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    {r.is_current
                      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>Current</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Superseded</span>}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : isFulfilment ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Client', 'Fulfilment fee £/parcel', 'Packaging markup', 'Shipping', 'Effective from', 'Current', 'Notes', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 600 }}>{r.client_name || 'Default (all clients)'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>£{Number(r.handling_fee_per_order).toFixed(2)}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{Number(r.packaging_markup_pct).toFixed(2)}%</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{r.shipping_pass_through ? 'Pass-through' : 'Included'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{r.effective_from ? new Date(r.effective_from).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    {r.is_current
                      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>Current</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Superseded</span>}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'right' }}><Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Btn></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Client', '£/L distilled', '£/L bottled', '£/hour', 'Min. run £', 'Bottling £/unit', 'Effective from', 'Current', 'Notes'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rates.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 600 }}>{r.client_name || 'Default (all clients)'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{r.rate_per_litre_distilled != null ? `£${Number(r.rate_per_litre_distilled).toFixed(4)}` : '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{r.rate_per_litre_bottled != null ? `£${Number(r.rate_per_litre_bottled).toFixed(4)}` : '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{r.rate_per_hour != null ? `£${Number(r.rate_per_hour).toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{r.minimum_run_charge != null ? `£${Number(r.minimum_run_charge).toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{r.bottling_rate_per_unit != null ? `£${Number(r.bottling_rate_per_unit).toFixed(4)}` : '—'}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{r.effective_from ? new Date(r.effective_from).toLocaleDateString('en-GB') : '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    {r.is_current
                      ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>Current</span>
                      : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Superseded</span>}
                  </td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BillingSection() {
  const { data, loading, refetch } = useApi('/commercial/storage-billing');
  const periods = Array.isArray(data) ? data : (data?.data ?? []);
  const [calculating, setCalculating] = useState(false);
  const [err, setErr] = useState('');

  async function calculate() {
    const month = (await uiPrompt('Calculate billing for month (YYYY-MM):'));
    if (!month) return;
    setCalculating(true); setErr('');
    try { await apiFetch('/commercial/storage-billing/calculate', { method: 'POST', body: { month } }); refetch(); }
    catch (e) { setErr(e.message); } finally { setCalculating(false); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Storage Billing Periods</h3>
        <Btn size="sm" onClick={calculate} disabled={calculating}>{calculating ? 'Calculating…' : 'Calculate Month'}</Btn>
      </div>
      {err && <p style={{ color: 'var(--status-critical)', fontSize: 13, marginBottom: 8 }}>{err}</p>}
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p> : periods.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No billing periods calculated yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Period', 'Client', 'Total (£)', 'Lines', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 600, fontFamily: 'monospace' }}>{p.billing_month || p.period_ref}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{p.client_name || '—'}</td>
                  <td style={{ padding: '9px 14px', fontWeight: 600 }}>£{Number(p.total_amount ?? 0).toFixed(2)}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{p.line_count ?? '—'}</td>
                  <td style={{ padding: '9px 14px' }}>
                    <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: p.status === 'invoiced' ? '#dcfce7' : '#fef9c3', color: p.status === 'invoiced' ? '#166534' : '#854d0e' }}>{p.status}</span>
                  </td>
                  <td style={{ padding: '9px 14px' }}>
                    {p.status !== 'invoiced' && (
                      <Btn size="sm" variant="ghost" onClick={async () => { try { await apiFetch(`/commercial/storage-billing/${p.id}/mark-invoiced`, { method: 'POST', body: {} }); refetch(); } catch (e) { alert(e.message); } }}>Mark invoiced</Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Production charges ────────────────────────────────────────────────────────
function CreateChargeModal({ onClose, onSaved }) {
  const { data: runsData } = useApi('/production');
  const runs = runsData?.runs ?? runsData?.data ?? [];
  const [runId, setRunId] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/commercial/production-charges', { method: 'POST', body: { production_run_id: runId } }); onSaved(); }
    catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <Modal title="Create Production Charge" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {err && <p style={{ color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{err}</p>}
        <Field label="Production run *">
          <SelectEl value={runId} onChange={setRunId} required>
            <option value="">Select run…</option>
            {runs.map(r => <option key={r.id} value={r.id}>{r.run_ref} — {r.recipe_name ?? r.client_name ?? ''}</option>)}
          </SelectEl>
        </Field>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>Charge lines will be calculated automatically from the run's production rate.</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving || !runId}>{saving ? 'Creating…' : 'Create Charge'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function ProductionChargesSection() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const qs = statusFilter ? `?status=${statusFilter}` : '';
  const { data, loading, refetch } = useApi(`/commercial/production-charges${qs}`, [statusFilter]);
  const charges = Array.isArray(data) ? data : (data?.data ?? []);
  const STATUS_C = { pending: { bg: '#fef9c3', color: '#854d0e' }, invoiced: { bg: '#dcfce7', color: '#166534' }, cancelled: { bg: '#f3f4f6', color: '#6b7280' } };
  async function updateStatus(id, status) {
    try { await apiFetch(`/commercial/production-charges/${id}/status`, { method: 'PATCH', body: { status } }); refetch(); }
    catch (e) { alert(e.message); }
  }
  return (
    <div style={{ marginBottom: 32 }}>
      {showCreate && <CreateChargeModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refetch(); }} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Contract Production Charges</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <SelectEl value={statusFilter} onChange={setStatusFilter}>
            <option value="">All statuses</option>
            {['pending','invoiced','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
          </SelectEl>
          <Btn size="sm" onClick={() => setShowCreate(true)}>+ New Charge</Btn>
        </div>
      </div>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p> : charges.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No charges found.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>
              {['Run','Client','Amount (£)','Status','Actions'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {charges.map(c => {
                const sc = STATUS_C[c.status] || { bg: '#f3f4f6', color: '#6b7280' };
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{c.run_ref}</td>
                    <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{c.client_name ?? '—'}</td>
                    <td style={{ padding: '9px 14px', fontWeight: 600 }}>£{Number(c.total_amount ?? 0).toFixed(2)}</td>
                    <td style={{ padding: '9px 14px' }}><span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: sc.bg, color: sc.color }}>{c.status}</span></td>
                    <td style={{ padding: '9px 14px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {c.status === 'pending' && <><Btn size="sm" variant="ghost" onClick={() => updateStatus(c.id, 'invoiced')}>Mark invoiced</Btn><Btn size="sm" variant="ghost" onClick={() => updateStatus(c.id, 'cancelled')}>Cancel</Btn></>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Xero account codes ────────────────────────────────────────────────────────
// Which Xero income account each kind of invoice line goes to.
const XERO_CHARGE_TYPES = {
  own_brand_sales: 'Trade sales (sales orders)',
  duty_recharge: 'Duty re-charged to clients',
  storage: 'Storage',
  fulfilment_handling: 'Fulfilment fees',
};
function XeroCodesSection() {
  const { data, loading, refetch } = useApi('/commercial/xero-account-codes');
  const codes = Array.isArray(data) ? data : (data?.data ?? []);
  const [form, setForm] = useState({ code: '', name: '', charge_type: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setErr('');
    try { await apiFetch('/commercial/xero-account-codes', { method: 'PUT', body: form }); setForm({ code: '', name: '', charge_type: '' }); refetch(); }
    catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Xero Account Codes</h3>
      {loading ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p> : codes.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>No account codes configured.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>
              {['Xero code','Name','Charge type'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontWeight: 700 }}>{c.code}</td>
                  <td style={{ padding: '9px 14px' }}>{c.name}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--text-muted)' }}>{XERO_CHARGE_TYPES[c.charge_type] || c.charge_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Add / update code</p>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>One code per charge type — saving a charge type replaces its code. Two charge types can use the same code.</p>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {err && <p style={{ width: '100%', color: 'var(--status-critical)', fontSize: 13, margin: 0 }}>{err}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Code *</label>
          <Input required value={form.code} onChange={set('code')} placeholder="e.g. 200" style={{ width: 90 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Name *</label>
          <Input required value={form.name} onChange={set('name')} placeholder="e.g. Storage Income" style={{ width: 180 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Charge type *</label>
          <select required value={form.charge_type} onChange={set('charge_type')} style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--surface-2)', fontSize: 14, width: 230 }}>
            <option value="">Choose…</option>
            {Object.entries(XERO_CHARGE_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <Btn type="submit" size="sm" disabled={saving}>{saving ? 'Saving…' : 'Save Code'}</Btn>
      </form>
    </div>
  );
}

function RatesTab() {
  return (
    <div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>Configure storage, fulfilment, and contract production rates. These are used when calculating client charges and billing periods.</p>
      <RatesSection title="Storage Rates" endpoint="/commercial/storage-rates" type="storage" />
      <RatesSection title="Fulfilment Rates" endpoint="/commercial/fulfilment-rates" type="fulfilment" />
      <RatesSection title="Contract Production Rates" endpoint="/commercial/production-rates" type="production" />
      <ProductionChargesSection />
      <XeroCodesSection />
      <BillingSection />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB: COMPANY (used to build outbound documents — Purchase Orders today)
// ═══════════════════════════════════════════════════════════════════════════════
const BLANK_COMPANY = {
  legal_name: '', trading_name: '', address: '', email: '', phone: '', website: '',
  vat_number: '', company_reg_number: '', excise_approval_ref: '', awrs_number: '', vehicle_regs: '',
  default_vat_rate: '20', default_payment_terms: '',
  bank_name: '', bank_account_name: '', bank_sort_code: '', bank_account_number: '', bank_iban: '', bank_swift: '',
  logo_data_url: '', po_terms: '',
  dpd_account_number: '', dpd_network_code: '12', dpd_saturday_network_code: '71', dpd_default_weight_kg: '2', dpd_description_label: 'items', dpd_email_separator: ';',
};

function CompanyTab() {
  const { data, loading, refetch } = useApi('/settings/company');
  const [form, setForm] = useState(BLANK_COMPANY);
  const [loaded, setLoaded] = useState(null); // the details as saved, to send only what changed
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  // useApi already unwraps { data }, so the settings row is `data` itself
  // (Joe, 22 Oct 2026: reading data.data left the form blank, and saving it
  // wiped the company details).
  useEffect(() => {
    const c = data?.data ?? data;
    if (c && c.id) {
      const f = { ...BLANK_COMPANY, ...Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v ?? ''])), default_vat_rate: c.default_vat_rate != null ? String(c.default_vat_rate) : '20' };
      setForm(f); setLoaded(f);
    }
  }, [data]);

  function handleLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { alert('Please use a smaller image (under 400KB) — it\'s embedded directly into documents.'); return; }
    const reader = new FileReader();
    reader.onload = () => set('logo_data_url', reader.result);
    reader.readAsDataURL(file);
  }

  async function handleSubmit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (!loaded && data === undefined) throw new Error('The company details didn\'t load — refresh the page before saving');
      // Only what was changed here — never blanks over details that didn't load.
      const body = loaded
        ? Object.fromEntries(Object.keys(BLANK_COMPANY).filter((k) => String(form[k] ?? '') !== String(loaded[k] ?? '')).map((k) => [k, form[k]]))
        : form;
      if (loaded && !Object.keys(body).length) { setSaved(true); return; }
      await apiFetch('/settings/company', { method: 'PUT', body });
      setSaved(true);
      refetch();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 720 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
        These details appear on outbound documents — Purchase Orders today, invoices and labels later.
      </p>

      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Identity</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              {form.logo_data_url
                ? <img src={form.logo_data_url} alt="Logo" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border)' }} />
                : <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--pld-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>PLD</div>}
              <label style={{ fontSize: 11.5, color: 'var(--pld-green)', fontWeight: 600, cursor: 'pointer' }}>
                {form.logo_data_url ? 'Change logo' : 'Upload logo'}
                <input type="file" accept="image/*" onChange={handleLogo} style={{ display: 'none' }} />
              </label>
            </div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Legal Name *"><Input value={form.legal_name} onChange={e => set('legal_name', e.target.value)} required /></Field>
              <Field label="Trading Name"><Input value={form.trading_name ?? ''} onChange={e => set('trading_name', e.target.value)} placeholder="If different from legal name" /></Field>
            </div>
          </div>
          <Field label="Address"><Textarea value={form.address ?? ''} onChange={v => set('address', v)} rows={2} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Email"><Input type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} /></Field>
            <Field label="Website"><Input value={form.website ?? ''} onChange={e => set('website', e.target.value)} /></Field>
          </div>
        </div>
      </div>

      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Registration & Duty</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="VAT Number"><Input value={form.vat_number ?? ''} onChange={e => set('vat_number', e.target.value)} /></Field>
          <Field label="Company Reg. Number"><Input value={form.company_reg_number ?? ''} onChange={e => set('company_reg_number', e.target.value)} /></Field>
          <Field label="Excise Approval Ref"><Input value={form.excise_approval_ref ?? ''} onChange={e => set('excise_approval_ref', e.target.value)} placeholder="AWRS / WOWGR ref" /></Field>
          <Field label="AWRS Number"><Input value={form.awrs_number ?? ''} onChange={e => set('awrs_number', e.target.value)} placeholder="e.g. XZAW00000120515" /></Field>
          <Field label="Our delivery vehicles" hint="Registrations, comma separated — ticked on despatch notes"><Input value={form.vehicle_regs ?? ''} onChange={e => set('vehicle_regs', e.target.value)} placeholder="e.g. LY22JXN, LV21 UDX" /></Field>
        </div>
      </div>

      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Order Defaults</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="Default VAT Rate (%)"><Input type="number" step="0.01" min="0" max="100" value={form.default_vat_rate ?? ''} onChange={e => set('default_vat_rate', e.target.value)} /></Field>
          <Field label="Payment terms"><p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', paddingTop: 8 }}>Set in Billing → Customer terms.</p></Field>
        </div>
        <Field label="PO Terms / Delivery Instructions">
          <Textarea value={form.po_terms ?? ''} onChange={v => set('po_terms', v)} rows={3}
            placeholder="Shown at the bottom of every Purchase Order. e.g. Please quote the PO number on your delivery note and invoice." />
        </Field>
      </div>

      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>DPD file <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>— for the import file uploaded to MyDPD Local (Orders → despatch run → DPD file)</span></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="DPD account number"><Input value={form.dpd_account_number ?? ''} onChange={e => set('dpd_account_number', e.target.value)} placeholder="e.g. 3031215" /></Field>
          <Field label="Network code (normal)"><Input value={form.dpd_network_code ?? ''} onChange={e => set('dpd_network_code', e.target.value)} placeholder="12" /></Field>
          <Field label="Network code (Saturday)"><Input value={form.dpd_saturday_network_code ?? ''} onChange={e => set('dpd_saturday_network_code', e.target.value)} placeholder="71" /></Field>
          <Field label="Default weight per unit (kg)"><Input type="number" step="0.01" min="0" value={form.dpd_default_weight_kg ?? ''} onChange={e => set('dpd_default_weight_kg', e.target.value)} placeholder="2" /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Description word"><Input value={form.dpd_description_label ?? ''} onChange={e => set('dpd_description_label', e.target.value)} placeholder="items → &quot;3 items&quot;" /></Field>
          <Field label="Email separator"><Input maxLength={2} value={form.dpd_email_separator ?? ''} onChange={e => set('dpd_email_separator', e.target.value)} placeholder=";" /></Field>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'end', paddingBottom: 6 }}>Weights per product and "ships on its own" are set on each stock item. Tracking-email copies are set per client.</div>
        </div>
      </div>

      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Bank Details <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>— shown on POs so suppliers have remittance details to hand</span></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <Field label="Bank Name"><Input value={form.bank_name ?? ''} onChange={e => set('bank_name', e.target.value)} /></Field>
          <Field label="Account Name"><Input value={form.bank_account_name ?? ''} onChange={e => set('bank_account_name', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <Field label="Sort Code"><Input value={form.bank_sort_code ?? ''} onChange={e => set('bank_sort_code', e.target.value)} /></Field>
          <Field label="Account Number"><Input value={form.bank_account_number ?? ''} onChange={e => set('bank_account_number', e.target.value)} /></Field>
          <Field label="IBAN"><Input value={form.bank_iban ?? ''} onChange={e => set('bank_iban', e.target.value)} /></Field>
          <Field label="SWIFT/BIC"><Input value={form.bank_swift ?? ''} onChange={e => set('bank_swift', e.target.value)} /></Field>
        </div>
      </div>

      {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Btn type="submit" disabled={saving || !form.legal_name.trim()}>{saving ? 'Saving…' : 'Save Company Settings'}</Btn>
        {saved && <span style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// TAB: INTEGRATIONS (on/off switches for Gmail, Xero, DPD, Shopify, HubSpot)
// ═══════════════════════════════════════════════════════════════════════
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 11, border: 'none', padding: 2,
        background: checked ? 'var(--pld-green)' : 'var(--border)',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
        transition: 'background .15s', display: 'inline-flex', alignItems: 'center',
        justifyContent: checked ? 'flex-end' : 'flex-start', flexShrink: 0,
      }}
    >
      <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.3)' }} />
    </button>
  );
}

// ── Xero connection (Joe, 27 Oct 2026) ──────────────────────────────────────
// Branding theme for Blake's invoices, and Xero's products moved from
// Brewman codes to Blake SKUs (Joe, 3 Nov 2026).
function XeroHousekeeping() {
  const { data, refetch } = useApi('/xero/branding-themes');
  const t = data?.data ?? data;
  const [busy, setBusy] = useState(''); const [err, setErr] = useState(''); const [note, setNote] = useState('');
  const [plan, setPlan] = useState(null); const [delDupes, setDelDupes] = useState(false);
  const run = async (key, fn) => { setBusy(key); setErr(''); setNote(''); try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(''); } };
  const sub = { fontWeight: 600, fontSize: 13.5, margin: '14px 0 4px' };
  const muted = { margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 };
  const list = (rows, f) => <div style={{ fontSize: 12.5, maxHeight: 180, overflowY: 'auto', margin: '4px 0', lineHeight: 1.6 }}>{rows.map((r, i) => <div key={i}>{f(r)}</div>)}</div>;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={sub}>Branding themes</div>
      <p style={muted}>Trade sales use one theme; contract clients' bills (duty, storage, fulfilment) the other. Until you choose, Blake uses the theme called "Standard" or "Contract".</p>
      {[['trade', 'Trade sales'], ['contract', 'Contract clients']].map(([kind, label]) => (
        <div key={kind} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, flexWrap: 'wrap', marginTop: 6 }}>
          <span style={{ width: 120 }}>{label}</span>
          <select value={t?.chosen?.[kind]?.id || ''} disabled={!!busy || !t} onChange={(e) => run(`theme-${kind}`, async () => { await apiFetch('/xero/branding-theme', { method: 'POST', body: { kind, theme_id: e.target.value || null } }); refetch(); setNote('Saved — new invoices use it.'); })} style={{ padding: '4px 8px', fontSize: 13 }}>
            <option value="">{t?.using?.[kind] ? `Automatic (${t.using[kind].name})` : "Xero's default"}</option>
            {(t?.themes || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      ))}

      <div style={sub}>Products in Xero</div>
      <p style={muted}>Xero's products can still have their Brewman codes. Blake can recode them to Blake SKUs (and Blake names), matched on each stock item's old SKU. Check first — nothing changes in Xero until you apply. Invoices already in Xero keep what they say.</p>
      <div style={{ marginTop: 6 }}>
        <Btn size="sm" variant="ghost" disabled={!!busy} onClick={() => run('plan', async () => setPlan((r => r.data ?? r)(await apiFetch('/xero/items-plan'))))}>{busy === 'plan' ? 'Checking Xero…' : 'Check products'}</Btn>
      </div>
      {plan && (
        <div style={{ marginTop: 8, fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 12 }}>
          <div>{plan.ok} already on Blake SKUs · <strong>{plan.rename.length}</strong> to recode · {plan.duplicate.length} Brewman duplicate{plan.duplicate.length === 1 ? '' : 's'} · {plan.unmatched.length} not matched (left alone)</div>
          {plan.rename.length > 0 && list(plan.rename, (r) => <>{r.from_code} <span style={{ color: 'var(--text-muted)' }}>{r.from_name}</span> → <strong>{r.to_code}</strong> {r.to_name}</>)}
          {plan.duplicate.length > 0 && (
            <>
              <div style={{ marginTop: 8 }}>Already in Xero under the Blake SKU too:</div>
              {list(plan.duplicate, (r) => <>{r.from_code} <span style={{ color: 'var(--text-muted)' }}>{r.from_name}</span> (Blake: {r.to_code})</>)}
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={delDupes} onChange={(e) => setDelDupes(e.target.checked)} /> Delete these Brewman duplicates from Xero</label>
            </>
          )}
          {plan.unmatched.length > 0 && (
            <details style={{ marginTop: 6 }}><summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>{plan.unmatched.length} Xero products with no matching stock item</summary>
              {list(plan.unmatched, (r) => <>{r.code} <span style={{ color: 'var(--text-muted)' }}>{r.name}</span></>)}
            </details>
          )}
          {plan.too_long?.length > 0 && <div style={{ color: '#92400e', marginTop: 6 }}>{plan.too_long.length} Blake SKU{plan.too_long.length === 1 ? ' is' : 's are'} longer than Xero allows (30 characters) — left alone.</div>}
          {(plan.rename.length > 0 || (delDupes && plan.duplicate.length > 0)) && (
            <div style={{ marginTop: 10 }}>
              <Btn size="sm" disabled={!!busy} onClick={async () => {
                if (!(await uiConfirm(`Recode ${plan.rename.length} product${plan.rename.length === 1 ? '' : 's'} in Xero${delDupes && plan.duplicate.length ? ` and delete ${plan.duplicate.length} duplicate${plan.duplicate.length === 1 ? '' : 's'}` : ''}?`, { okLabel: 'Apply in Xero' }))) return;
                run('apply', async () => {
                  const r = (x => x.data ?? x)(await apiFetch('/xero/items-apply', { method: 'POST', body: { delete_duplicates: delDupes } }));
                  setPlan(r.plan);
                  setNote(`Recoded ${r.renamed.length}${r.deleted.length ? `, deleted ${r.deleted.length}` : ''}.`);
                  if (r.failed.length) setErr(`Didn't go through: ${r.failed.join('; ')}`);
                });
              }}>{busy === 'apply' ? 'Updating Xero…' : 'Apply in Xero'}</Btn>
            </div>
          )}
        </div>
      )}
      {note && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#166534' }}>{note}</p>}
      {err && <p style={{ margin: '8px 0 0', fontSize: 13, color: '#dc2626' }}>{err}</p>}
    </div>
  );
}

function XeroPanel() {
  const { data, loading, refetch } = useApi('/xero/status');
  const x = data?.data ?? data;
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const [msg] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('xero');
    if (!q) return null;
    const org = new URLSearchParams(window.location.search).get('org');
    return q === 'connected' ? { ok: true, text: `Connected to ${org || 'Xero'}.` } : { ok: false, text: q };
  });
  async function connect() {
    setBusy(true); setErr('');
    try { const r = await apiFetch('/xero/connect-url'); window.location.href = (r.data ?? r).url; } catch (e) { setErr(e.message); setBusy(false); }
  }
  async function disconnect() {
    if (!(await uiConfirm('Disconnect Blake from Xero? Invoices stop going to Xero until you connect again.', { okLabel: 'Disconnect', danger: true }))) return;
    setBusy(true); setErr('');
    try { await apiFetch('/xero/disconnect', { method: 'POST' }); refetch(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  const [appForm, setAppForm] = useState(null); // { client_id, client_secret } while editing
  async function saveApp(e) {
    e.preventDefault(); setBusy(true); setErr('');
    try { await apiFetch('/xero/app', { method: 'POST', body: appForm }); setAppForm(null); refetch(); } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  }
  async function pick(id) {
    setBusy(true); setErr('');
    try { await apiFetch('/xero/tenant', { method: 'POST', body: { tenant_id: id } }); refetch(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  if (loading && !x) return null;
  if (!x) return null;
  const box = { border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 24, background: 'var(--surface)' };
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Xero</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: x.connected ? '#dcfce7' : '#f3f4f6', color: x.connected ? '#166534' : 'var(--text-muted)' }}>
          {x.connected ? `Connected${x.tenant_name ? ` to ${x.tenant_name}` : ''}` : 'Not connected'}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {x.app_set_up && <Btn size="sm" variant={x.connected ? 'ghost' : 'primary'} disabled={busy} onClick={connect}>{x.connected ? 'Reconnect' : 'Connect to Xero'}</Btn>}
          {x.connected && <Btn size="sm" variant="ghost" disabled={busy} onClick={disconnect}>Disconnect</Btn>}
        </span>
      </div>
      {msg && <p style={{ margin: '10px 0 0', fontSize: 13, color: msg.ok ? '#166534' : '#dc2626' }}>{msg.text}</p>}
      {err && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626' }}>{err}</p>}
      {x.connected && x.tenants?.length > 1 && (
        <div style={{ marginTop: 10, fontSize: 13 }}>Organisation:{' '}
          <select value={x.tenant_id || ''} onChange={(e) => pick(e.target.value)} disabled={busy} style={{ padding: '4px 8px', fontSize: 13 }}>
            {x.tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}
      <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Sends draft invoices to Xero for someone to check and approve there: trade sales orders when they're despatched, contract clients' duty releases,
        monthly storage billing (Billing page) and monthly fulfilment bills. Set which Xero account each kind of line goes to under Rates & Billing → Xero account codes.
      </p>
      {x.connected && <XeroHousekeeping />}
      {(!x.app_set_up || appForm) ? (
        <div style={{ marginTop: 12, fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 12, lineHeight: 1.55 }}>
          <strong>{x.app_set_up ? 'Change the Xero app details' : 'To set up (once):'}</strong>
          <ol style={{ margin: '6px 0 10px', paddingLeft: 20 }}>
            <li>At <a href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer">developer.xero.com</a> → My Apps → New app → <em>Web app</em>. Company URL: <code>https://blake.pleasantlanddistillery.co.uk</code></li>
            <li>Redirect URI: <code style={{ userSelect: 'all' }}>{x.redirect_uri}</code></li>
            <li>On the app's <strong>Configuration</strong> page, copy the Client ID, click <em>Generate a secret</em>, and paste both here.</li>
          </ol>
          <form onSubmit={saveApp} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 220px' }}>Client ID
              <input value={appForm?.client_id ?? ''} onChange={(e) => setAppForm((f) => ({ ...(f || {}), client_id: e.target.value }))} required autoComplete="off" style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontFamily: 'monospace' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 220px' }}>Client secret
              <input type="password" value={appForm?.client_secret ?? ''} onChange={(e) => setAppForm((f) => ({ ...(f || {}), client_secret: e.target.value }))} required autoComplete="new-password" style={{ padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontFamily: 'monospace' }} />
            </label>
            <Btn size="sm" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Btn>
            {x.app_set_up && <Btn size="sm" variant="ghost" type="button" onClick={() => setAppForm(null)}>Cancel</Btn>}
          </form>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>The secret is stored encrypted and never shown again. Then click <strong>Connect to Xero</strong>.</p>
        </div>
      ) : (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
          Xero app {x.client_id_hint}{x.app_from === 'env' ? ' (from the server settings file)' : ''} ·{' '}
          <button type="button" onClick={() => setAppForm({ client_id: '', client_secret: '' })} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12, textDecoration: 'underline' }}>change app details</button>
        </p>
      )}
    </div>
  );
}

// Shopify connected through an app (Joe, 3 Nov 2026): orders in, despatched
// orders marked fulfilled, and Shopify's stock following Blake's.
const SHOPIFY_SCOPES = 'read_orders, read_products, read_inventory, write_inventory, read_locations, read_merchant_managed_fulfillment_orders, write_merchant_managed_fulfillment_orders';
function ShopifyPanel() {
  const { data, loading, refetch } = useApi('/shopify');
  const s = data?.data ?? data;
  const [busy, setBusy] = useState(''); const [err, setErr] = useState(''); const [note, setNote] = useState('');
  const [form, setForm] = useState(null);
  const run = async (key, fn, done) => {
    setBusy(key); setErr(''); setNote('');
    try { const r = await fn(); if (done) setNote(done(r?.data ?? r)); refetch(); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  if (loading && !s) return null;
  if (!s) return null;
  const box = { border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 24, background: 'var(--surface)' };
  const sub = { fontWeight: 600, fontSize: 13.5, margin: '14px 0 4px' };
  const muted = { margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 };
  const link = { background: 'none', border: 'none', padding: 0, color: 'var(--pld-green)', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline' };
  const inp = { padding: '8px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontFamily: 'monospace' };
  const last = s.last_stock_sync;
  const fmtT = (t) => t ? new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const editing = !s.connected || form;
  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>Shopify</span>
        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999, background: s.connected && !s.error ? '#dcfce7' : '#f3f4f6', color: s.connected && !s.error ? '#166534' : 'var(--text-muted)' }}>
          {s.connected ? (s.error ? 'Connected — but not answering' : `Connected to ${s.shop_name || s.shop}`) : 'Not connected'}
        </span>
      </div>
      {s.error && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626' }}>{s.error}</p>}
      {err && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#dc2626' }}>{err}</p>}
      {note && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#166534' }}>{note}</p>}

      {editing ? (
        <div style={{ marginTop: 12, fontSize: 13, background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 12, lineHeight: 1.55 }}>
          <strong>{s.connected ? 'Change the Shopify app details' : 'To set up (once):'}</strong>
          <ol style={{ margin: '6px 0 10px', paddingLeft: 20 }}>
            <li>In Shopify admin → <em>Settings → Apps → Develop apps</em>, open the <strong>Dev Dashboard</strong> and create an app called Blake. App URL: <code>https://blake.pleasantlanddistillery.co.uk</code></li>
            <li>Give it these access scopes: <code style={{ userSelect: 'all' }}>{SHOPIFY_SCOPES}</code>, then release that version.</li>
            <li>Install the app on the store.</li>
            <li>From the app's <strong>Settings</strong>, copy the Client ID and Client secret and paste them here, with the store's address.</li>
          </ol>
          <form onSubmit={(e) => { e.preventDefault(); run('save', () => apiFetch('/shopify/app', { method: 'POST', body: form }), () => { setForm(null); return 'Connected.'; }); }}
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>Store address
              <input value={form?.shop ?? ''} onChange={(e) => setForm((f) => ({ ...(f || {}), shop: e.target.value }))} required placeholder="your-store.myshopify.com" style={inp} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>Client ID
              <input value={form?.client_id ?? ''} onChange={(e) => setForm((f) => ({ ...(f || {}), client_id: e.target.value }))} autoComplete="off" style={inp} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>Client secret
              <input type="password" value={form?.client_secret ?? ''} onChange={(e) => setForm((f) => ({ ...(f || {}), client_secret: e.target.value }))} autoComplete="new-password" style={inp} />
            </label>
            <Btn size="sm" type="submit" disabled={!!busy}>{busy === 'save' ? 'Checking…' : 'Connect'}</Btn>
            {s.connected && <Btn size="sm" variant="ghost" type="button" onClick={() => setForm(null)}>Cancel</Btn>}
          </form>
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Already have an older custom app with an Admin API access token?</summary>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6, maxWidth: 420 }}>Admin API access token (shpat_…) — instead of the Client ID and secret
              <input type="password" value={form?.access_token ?? ''} onChange={(e) => setForm((f) => ({ ...(f || {}), access_token: e.target.value }))} autoComplete="new-password" style={inp} />
            </label>
          </details>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>Blake checks the details with Shopify before saving. The secret is stored encrypted and never shown again.</p>
        </div>
      ) : (
        <>
          <div style={sub}>Orders coming in</div>
          <p style={muted}>
            {s.webhooks_set_up_at ? <>Shopify sends each paid or cancelled order to Blake (set up {fmtT(s.webhooks_set_up_at)}). </> : <>Shopify needs telling to send paid and cancelled orders to Blake. </>}
            <button type="button" style={link} disabled={!!busy} onClick={() => run('hooks', () => apiFetch('/shopify/webhooks', { method: 'POST' }), (r) => r.made?.length ? `Set up: ${r.made.join(', ')}.` : 'Already set up.')}>
              {busy === 'hooks' ? 'Setting up…' : s.webhooks_set_up_at ? 'Check them again' : 'Set it up now'}
            </button>
          </p>
          <p style={muted}>
            {s.last_webhook
              ? <>Last heard from Shopify {fmtT(s.last_webhook.created_at)}{s.last_webhook.order_name ? ` (${s.last_webhook.order_name})` : ''}: {s.last_webhook.process_error || s.last_webhook.status}.</>
              : <>Blake hasn't heard from Shopify yet.</>}
            {' '}Blake also asks Shopify every hour for paid orders that didn't arrive, and Orders has "Check Shopify" to look now.
          </p>

          <div style={sub}>Despatched orders</div>
          <p style={muted}>When a Shopify order is despatched in Blake, it's marked fulfilled in Shopify (carrier DPD Local, with the tracking number once Blake has one) and Shopify emails the customer.</p>
          {s.fulfil_problems?.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 13 }}>
              {s.fulfil_problems.map((p) => (
                <div key={p.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', color: '#92400e' }}>
                  <span>{p.external_ref || p.order_ref} ({p.order_ref}) — didn't go through: {p.shopify_fulfil_error}</span>
                  <button type="button" style={link} disabled={!!busy} onClick={() => run(`f${p.id}`, () => apiFetch(`/shopify/fulfil/${p.id}`, { method: 'POST' }), () => `${p.order_ref} marked fulfilled in Shopify.`)}>Try again</button>
                </div>
              ))}
            </div>
          )}

          <div style={sub}>Stock</div>
          <p style={muted}>Shopify's stock for each product follows what Blake has available (matched on SKU), checked every hour (or press Sync now). A sale Blake hasn't heard about yet is never written over.</p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: 13 }}>
            <label>Shopify location{' '}
              <select value={s.location_id || ''} disabled={!!busy} onChange={(e) => run('loc', () => apiFetch('/shopify/location', { method: 'POST', body: { location_id: e.target.value } }))} style={{ padding: '4px 8px', fontSize: 13 }}>
                {!s.location_id && <option value="">Choose…</option>}
                {(s.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}{l.online ? '' : ' (not for online orders)'}</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={s.stock_sync} disabled={!!busy || !s.location_id} onChange={(e) => run('auto', () => apiFetch('/shopify/stock-sync', { method: 'POST', body: { on: e.target.checked } }))} />
              Keep Shopify's stock in step with Blake
            </label>
            <Btn size="sm" variant="ghost" disabled={!!busy || !s.location_id} onClick={() => run('sync', () => apiFetch('/shopify/stock-sync/run', { method: 'POST' }), (r) => `Checked ${r.products} Shopify products: ${r.updated.length} updated.`)}>
              {busy === 'sync' ? 'Syncing…' : 'Sync now'}
            </Btn>
          </div>
          {last && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              <div>Last sync {fmtT(last.at)}: {last.products} products, {last.updated.length} updated{last.waiting?.length ? `, ${last.waiting.length} left for the next run (just sold)` : ''}.</div>
              {last.updated.length > 0 && <div>{last.updated.slice(0, 8).map((u) => `${u.sku} ${u.from}→${u.to}`).join(' · ')}{last.updated.length > 8 ? ` · +${last.updated.length - 8} more` : ''}</div>}
              {last.not_in_blake_count > 0 && (
                <details><summary style={{ cursor: 'pointer' }}>{last.not_in_blake_count} Shopify product{last.not_in_blake_count === 1 ? '' : 's'} with a SKU Blake doesn't have — left alone</summary>{last.not_in_blake.join(', ')}</details>
              )}
              {last.untracked?.length > 0 && <details><summary style={{ cursor: 'pointer' }}>{last.untracked.length} not tracked for stock in Shopify — left alone</summary>{last.untracked.join(', ')}</details>}
              {last.not_stocked_at_location?.length > 0 && <details><summary style={{ cursor: 'pointer' }}>{last.not_stocked_at_location.length} not stocked at {s.location_name} in Shopify</summary>{last.not_stocked_at_location.join(', ')}</details>}
              {last.errors?.length > 0 && <div style={{ color: '#dc2626' }}>{last.errors.join('; ')}</div>}
            </div>
          )}

          <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            {s.shop} · app {s.client_id_hint}{s.from === 'env' ? ' (from the server settings file)' : ''} ·{' '}
            <button type="button" style={{ ...link, fontSize: 12 }} onClick={() => setForm({ shop: s.shop })}>change app details</button>{' · '}
            <button type="button" style={{ ...link, fontSize: 12 }} disabled={!!busy} onClick={async () => {
              if (!(await uiConfirm('Disconnect Blake from Shopify? Fulfilments and stock stop going to Shopify. Orders sent by webhooks set up by hand keep coming in.', { okLabel: 'Disconnect', danger: true }))) return;
              run('off', () => apiFetch('/shopify/app', { method: 'DELETE' }));
            }}>disconnect</button>
          </p>
        </>
      )}
    </div>
  );
}

function IntegrationsTab() {
  const { data, loading, error, refetch } = useApi('/settings/integrations');
  const [busyKey, setBusyKey] = useState(null);
  const [rowError, setRowError] = useState('');

  async function handleToggle(integration, enabled) {
    setBusyKey(integration); setRowError('');
    try {
      await apiFetch(`/settings/integrations/${integration}`, { method: 'PUT', body: { enabled } });
      refetch();
    } catch (err) {
      setRowError(err.message);
    } finally {
      setBusyKey(null);
    }
  }

  if (loading) return <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>;
  if (error) return <p style={{ color: '#dc2626', fontSize: 14 }}>{error.message || 'Failed to load'}</p>;

  const integrations = data?.data ?? data ?? [];

  return (
    <div>
      <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: 14, maxWidth: 640 }}>
        Switch an integration off to stop Blake from using it entirely — the action is
        blocked everywhere in the app, not just hidden, until it's switched back on.
        This is independent of whether an integration's credentials are configured.
      </p>
      <XeroPanel />
      <ShopifyPanel />
      {rowError && <p style={{ margin: '0 0 16px', color: '#dc2626', fontSize: 13 }}>{rowError}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {integrations.map((row) => (
          <div key={row.integration} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{row.label}</span>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999,
                  background: row.configured ? '#dcfce7' : '#f3f4f6',
                  color: row.configured ? '#166534' : 'var(--text-muted)',
                }}>
                  {row.configured ? 'Configured' : 'Not configured'}
                </span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{row.description}</p>
            </div>
            <Toggle checked={row.enabled} disabled={busyKey === row.integration} onChange={(next) => handleToggle(row.integration, next)} />
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// TAB: PRINTERS — the shipping-PC print helper and which printer each
// document goes to. See print-agent/README.md for setting up the PC.
// ═══════════════════════════════════════════════════════════════════════════════
function timeAgo(ts) {
  if (!ts) return 'never';
  const s = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ts).toLocaleDateString('en-GB');
}

function PrintersTab() {
  const [agents, setAgents] = useState(null);
  const [routes, setRoutes] = useState(null);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState({}); // docType -> message

  const load = useCallback(async () => {
    try {
      const [a, r] = await Promise.all([apiFetch('/print/agents'), apiFetch('/print/routes')]);
      setAgents(a); setRoutes(r); setError('');
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const agent = agents?.[0] || null;
  const printers = agent?.printers || [];

  async function createAgent() {
    setBusy(true);
    try {
      const res = await apiFetch('/print/agents', { method: 'POST', body: { name: 'Shipping PC' } });
      setNewKey(res.token); await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function rotateKey() {
    if (!(await uiConfirm('Make a new key? The print helper stops working until its config.json has the new key.'))) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/print/agents/${agent.id}/rotate`, { method: 'POST' });
      setNewKey(res.token); await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function removeAgent() {
    if (!(await uiConfirm('Remove this print helper? Direct printing stops until a helper is set up again.'))) return;
    setBusy(true);
    try { await apiFetch(`/print/agents/${agent.id}`, { method: 'DELETE' }); setNewKey(null); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function saveRoute(docType, printerName, copies) {
    try {
      const r = await apiFetch(`/print/routes/${docType}`, { method: 'PUT', body: { agent_id: agent.id, printer_name: printerName, copies } });
      setRoutes(r);
    } catch (e) { setError(e.message); }
  }

  async function testPrint(docType, printerName) {
    setTestState(s => ({ ...s, [docType]: 'Sending…' }));
    try {
      const job = await apiFetch('/print/test', { method: 'POST', body: { agent_id: agent.id, printer_name: printerName } });
      for (let i = 0; i < 25; i++) {
        await new Promise(r => setTimeout(r, 1000));
        const j = await apiFetch(`/print/jobs/${job.id}`);
        if (j.status === 'completed') { setTestState(s => ({ ...s, [docType]: '✓ Printed' })); return; }
        if (j.status === 'failed' || j.status === 'expired') { setTestState(s => ({ ...s, [docType]: `Failed: ${j.error_message || j.status}` })); return; }
      }
      setTestState(s => ({ ...s, [docType]: 'No reply from the shipping PC yet' }));
    } catch (e) { setTestState(s => ({ ...s, [docType]: e.message })); }
  }

  if (!agents && !error) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading printers…</div>;

  const card = { border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20, background: 'var(--surface)' };

  return (
    <div>
      {error && <p style={{ color: 'var(--status-critical)', fontSize: 13 }}>{error}</p>}

      <div style={card}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Shipping PC print helper</h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          A small program on the shipping PC that prints whatever is sent from Blake's 🖨 Print buttons, on the printer chosen below.
          Nothing prints unless someone clicks Print. Setup steps are in <code>print-agent/README.md</code>.
        </p>

        {!agent && <Btn onClick={createAgent} disabled={busy}>Set up shipping PC</Btn>}

        {agent && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: agent.online ? '#16a34a' : '#dc2626' }} />
              {agent.name} — {agent.online ? 'Connected' : 'Not connected'}
            </span>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {agent.hostname ? `${agent.hostname} · ` : ''}last seen {timeAgo(agent.last_seen_at)}{agent.agent_version ? ` · v${agent.agent_version}` : ''} · {printers.length} printer{printers.length === 1 ? '' : 's'} found
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="ghost" onClick={rotateKey} disabled={busy}>New key</Btn>
              <Btn size="sm" variant="ghost" onClick={removeAgent} disabled={busy} style={{ color: '#dc2626' }}>Remove</Btn>
            </span>
          </div>
        )}

        {newKey && (
          <div style={{ marginTop: 16, padding: 14, borderRadius: 'var(--radius)', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
            <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600 }}>Helper key — paste this into setup.ps1 on the shipping PC. It's only shown once.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input readOnly value={newKey} onFocus={e => e.target.select()} style={{ fontFamily: 'monospace', fontSize: 12 }} />
              <Btn size="sm" variant="ghost" onClick={() => navigator.clipboard?.writeText(newKey)}>Copy</Btn>
            </div>
          </div>
        )}
      </div>

      {agent && (
        <div style={card}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700 }}>Which printer for each document</h3>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-muted)' }}>
            Documents with no printer set keep the normal Preview/print-dialog only.
            {!printers.length && ' The printer list appears once the helper has connected.'}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Document', 'Printer', 'Copies', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(routes || []).map(r => {
                const known = printers.some(p => p.name === r.printer_name);
                return (
                  <tr key={r.doc_type} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{r.label}</td>
                    <td style={{ padding: '10px', minWidth: 240 }}>
                      <SelectEl value={r.printer_name || ''} onChange={v => saveRoute(r.doc_type, v, r.copies)}>
                        <option value="">— Not set (Preview only) —</option>
                        {printers.map(p => <option key={p.name} value={p.name}>{p.name}{p.isDefault ? ' (default)' : ''}</option>)}
                        {r.printer_name && !known && <option value={r.printer_name}>{r.printer_name} (not found on PC)</option>}
                      </SelectEl>
                    </td>
                    <td style={{ padding: '10px', width: 80 }}>
                      <Input type="number" min="1" max="20" value={r.copies}
                        disabled={!r.printer_name}
                        onChange={e => setRoutes(rs => rs.map(x => x.doc_type === r.doc_type ? { ...x, copies: e.target.value } : x))}
                        onBlur={e => r.printer_name && saveRoute(r.doc_type, r.printer_name, e.target.value)} />
                    </td>
                    <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                      {r.printer_name && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <Btn size="sm" variant="ghost" onClick={() => testPrint(r.doc_type, r.printer_name)} disabled={!agent.online}>Test</Btn>
                          {testState[r.doc_type] && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{testState[r.doc_type]}</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// MAIN SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'users',        label: '👥 Users' },
  { key: 'permissions',  label: '🔑 Permissions' },
  { key: 'two_fa',       label: '🔐 My 2FA' },
  { key: 'warehouse',    label: '🏭 Warehouse' },
  { key: 'rates',        label: '💷 Rates & Billing' },
  { key: 'company',      label: '🏢 Company' },
  { key: 'integrations', label: '🔌 Integrations' },
  { key: 'printers',     label: '🖨 Printers' },
];

export default function Settings() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'users');
  const [currentUser, setCurrentUser] = useState(null);

  const loadCurrentUser = useCallback(async () => {
    try {
      const res = await apiFetch('/settings/me');
      setCurrentUser(res.data ?? res);
    } catch (_) {}
  }, []);

  useEffect(() => { loadCurrentUser(); }, [loadCurrentUser]);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Settings</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>User management, permissions, and security</p>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 28 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--pld-green)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--pld-green)' : '2px solid transparent',
              marginBottom: -1, transition: 'color .15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'users'       && <UsersTab currentUser={currentUser} />}
      {tab === 'permissions' && <PermissionsTab currentUser={currentUser} />}
      {tab === 'two_fa'      && <TwoFATab currentUser={currentUser} onUserUpdate={loadCurrentUser} />}
      {tab === 'warehouse'   && <WarehouseTab />}
      {tab === 'rates'       && <RatesTab />}
      {tab === 'company'     && <CompanyTab />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'printers'    && <PrintersTab />}
    </div>
  );
}
